"use client"

import type { TrackingContext } from "@/modules/tracking/types"
import type { FunctionComponent, MouseEvent } from "react"

import { useEffect, useRef, useState } from "react"

import { IconButton } from "@jobteaser/spark/components/IconButton"
import { StarFilled } from "@jobteaser/spark/components/Icons/StarFilled"
import { StarOutlined } from "@jobteaser/spark/components/Icons/StarOutlined"

import { getCompanyFollower } from "@/modules/companies/api/endpoints/companyFollower"
import { setFollowCompanyStatus } from "@/modules/companies/api/endpoints/setFollowCompanyStatus"
import { useNotification } from "@/modules/notification/hooks"
import { useTracking } from "@/modules/tracking/useTracking"
import { useIntersectionObserver } from "@/modules/window/hooks/useIntersectionObserver"

type FollowButtonProps = {
  className?: string
  companyId: string
  errorMessage: string
  followLabel: string
  permalink: string
  schoolId?: string
  title?: string
  trackingContext: TrackingContext
  unFollowLabel: string
}

export const FollowButton: FunctionComponent<FollowButtonProps> = ({
  className,
  companyId,
  errorMessage,
  followLabel,
  permalink,
  schoolId,
  title,
  trackingContext,
  unFollowLabel,
}) => {
  const tracking = useTracking()
  const showNotification = useNotification()

  const ref = useRef<HTMLButtonElement>(null)
  const [followStatus, setFollowStatus] = useState<boolean | null>(null)
  const isObserved = useIntersectionObserver(ref, { threshold: 0.8 })

  useEffect(() => {
    if (isObserved && typeof followStatus !== "boolean") {
      getCompanyFollower({
        permalink,
      }).then(({ userInfo }) => {
        setFollowStatus(userInfo?.followsCompany || false)
      })
    }
  }, [followStatus, permalink, isObserved])

  const handleClick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.stopPropagation()

    if (typeof followStatus !== "boolean") {
      return
    }

    try {
      const currentValue = !!followStatus
      const nextValue = !followStatus

      await setFollowCompanyStatus({
        company_id: companyId,
        follow: nextValue,
      })

      setFollowStatus(nextValue)
      tracking?.track("company_page_follow_action", {
        action: currentValue ? "unfollow" : "follow",
        company_page_id: companyId,
        context: trackingContext,
        service: "companies",
        trackee_school_id: schoolId,
      })
    } catch {
      showNotification({
        title: errorMessage,
        variant: "error",
      })
    }
  }

  return (
    <IconButton
      aria-label={followStatus ? unFollowLabel : followLabel}
      className={className}
      aria-readonly={typeof followStatus !== "boolean"}
      disabled={typeof followStatus !== "boolean"}
      title={title}
      icon={followStatus ? StarFilled : StarOutlined}
      onClick={handleClick}
      ref={ref}
    />
  )
}
